import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Phone, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * EntryContactModal
 * -----------------
 * Blocking modal shown to every visitor on site entry. Captures Name + Mobile
 * BEFORE they see/use the calculator. This maximises lead capture — we save a
 * lead even if the visitor never reaches the PDF download step.
 *
 * Props:
 *  - open (boolean)          : whether modal is visible
 *  - onSuccess(leadId)       : called with the new lead id after successful save
 *  - onFailSilently()        : called when the backend request fails unexpectedly
 *                              (e.g. network error) — lets the visitor into the site
 *                              to preserve UX. Lead capture can be retried on download.
 */
export const EntryContactModal = ({ open, onSuccess, onFailSilently }) => {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [duplicateMsg, setDuplicateMsg] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim() || name.trim().length < 2) {
      newErrors.name = 'Please enter your full name';
    }
    const mobilePattern = /^[6-9]\d{9}$/;
    if (!mobile.trim()) {
      newErrors.mobile = 'Please enter your mobile number';
    } else if (!mobilePattern.test(mobile.trim())) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMobileInput = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setMobile(value);
      // Clear duplicate message when user edits the number
      if (duplicateMsg) setDuplicateMsg('');
      if (errors.mobile) setErrors((prev) => ({ ...prev, mobile: undefined }));
    }
  };

  const handleNameInput = (e) => {
    setName(e.target.value);
    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setDuplicateMsg('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads`, {
        name: name.trim(),
        mobile: mobile.trim(),
      });
      if (response.data?.success && response.data?.lead_id) {
        onSuccess(response.data.lead_id);
      } else {
        // Unexpected shape — fail silently to not block the user
        onFailSilently?.();
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Duplicate mobile — user MUST use a different number
        setDuplicateMsg(
          err?.response?.data?.detail ||
            'This number is already registered. Please use another number.',
        );
      } else {
        // Network or server error — fail silently, let them into the site
        console.error('Entry lead capture failed:', err);
        onFailSilently?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Blocking modal — explicitly block close attempts (esc/outside click)
  const handleOpenChange = () => {
    // no-op: cannot close manually
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="entry-contact-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-accent/15 text-accent">
              <Sparkles size={18} />
            </div>
            <DialogTitle className="text-2xl font-bold text-primary">
              Welcome!
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm">
            Please share your details to use the Wealth Sustainability Calculator.
            We&apos;ll send you a personalised report and financial insights.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-3">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="entry-name" className="text-sm font-medium">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <User
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id="entry-name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={handleNameInput}
                className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                data-testid="entry-contact-name"
                autoFocus
              />
            </div>
            {errors.name && (
              <p className="text-xs text-red-500" data-testid="entry-contact-name-error">
                {errors.name}
              </p>
            )}
          </div>

          {/* Mobile */}
          <div className="space-y-2">
            <Label htmlFor="entry-mobile" className="text-sm font-medium">
              Mobile Number <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Phone
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id="entry-mobile"
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={mobile}
                onChange={handleMobileInput}
                className={`pl-10 ${errors.mobile || duplicateMsg ? 'border-red-500' : ''}`}
                maxLength={10}
                data-testid="entry-contact-mobile"
                inputMode="numeric"
              />
            </div>
            {errors.mobile && (
              <p className="text-xs text-red-500" data-testid="entry-contact-mobile-error">
                {errors.mobile}
              </p>
            )}
            {duplicateMsg && (
              <p
                className="text-xs text-red-600 font-medium"
                data-testid="entry-contact-duplicate-error"
              >
                {duplicateMsg}
              </p>
            )}
          </div>

          {/* Disclaimer */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-900 leading-relaxed">
              By continuing, you agree to receive communications about financial
              planning and investment opportunities from Sahi Disha Financial
              Services.
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold"
            data-testid="entry-contact-submit"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue to Calculator'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EntryContactModal;
